import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import {
  LayoutDashboard, Camera, Compass, ClipboardCheck, AlertTriangle, CalendarDays, TrendingUp,
  Calculator, FileQuestion, FileCheck2, Package, FileText, ShieldCheck, HardHat, Users, Settings2,
  PencilRuler, Boxes, Sparkles, Bot, ChevronRight, CircleAlert, Clock, Inbox, RefreshCw,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GROUPS = [
  { title: 'Overview', items: [
    { label: 'Project Overview', icon: LayoutDashboard, view: 'overview' },
    { label: 'My Day', icon: CalendarDays, view: 'myday' },
    { label: 'Project Exceptions', icon: CircleAlert, view: 'exceptions' },
  ]},
  { title: 'Field', items: [
    { label: 'Capture / Visual Intelligence', icon: Camera, to: 'object-intel' },
    { label: 'Uploads', icon: Boxes, to: 'uploads' },
    { label: '360 Site Walk', icon: Compass, to: 'site-walks' },
    { label: 'Issues', icon: AlertTriangle, to: 'issues' },
  ]},
  { title: 'Project Controls', items: [
    { label: 'Schedule', icon: CalendarDays, to: 'schedule' },
    { label: 'Progress', icon: TrendingUp, to: 'progress' },
    { label: 'BOQ', icon: Calculator, to: 'boq' },
    { label: 'RFI', icon: FileQuestion, to: 'controls', tab: 'rfi' },
    { label: 'Submittals', icon: FileCheck2, to: 'controls', tab: 'submittals' },
    { label: 'Material Approval (MIR)', icon: Package, to: 'controls', tab: 'mir' },
    { label: 'Method Statements', icon: FileText, to: 'controls', tab: 'ms' },
    { label: 'ITP', icon: ClipboardCheck, to: 'controls', tab: 'itp' },
  ]},
  { title: 'Quality & Safety', items: [
    { label: 'Quality', icon: ClipboardCheck, to: 'quality' },
    { label: 'Safety', icon: ShieldCheck, to: 'safety' },
    { label: 'PTW', icon: HardHat, to: 'safety-controls', tab: 'ptw' },
    { label: 'JSA/JHA', icon: HardHat, to: 'safety-controls', tab: 'jsa' },
    { label: 'Toolbox Talks', icon: Users, to: 'safety-controls', tab: 'toolbox' },
    { label: 'Incidents', icon: AlertTriangle, to: 'safety-controls', tab: 'incidents' },
  ]},
  { title: 'Design', items: [
    { label: 'Drawings', icon: PencilRuler, to: 'reference-drawing' },
    { label: 'Design Intelligence / BIM', icon: Boxes, to: 'design' },
  ]},
  { title: 'Intelligence', items: [
    { label: 'Analytics / Reports', icon: Sparkles, to: 'reports' },
    { label: 'Construction Copilot', icon: Bot, view: 'copilot' },
  ]},
  { title: 'Administration', items: [
    { label: 'Construction Structure', icon: Settings2, to: 'construction' },
    { label: 'Version History', icon: Clock, to: 'history' },
    { label: 'Cost Codes', icon: Calculator, to: 'cost-codes' },
  ], adminOnly: true },
];

const SECTION_META = {
  OVERDUE: { label: 'Overdue', color: 'text-rose-700 bg-rose-50 border-rose-100', icon: AlertTriangle },
  TODAY: { label: 'Today', color: 'text-amber-700 bg-amber-50 border-amber-100', icon: CalendarDays },
  REQUIRES_MY_ACTION: { label: 'Requires My Action', color: 'text-indigo-700 bg-indigo-50 border-indigo-100', icon: Inbox },
  REVIEWS: { label: 'Reviews', color: 'text-sky-700 bg-sky-50 border-sky-100', icon: FileCheck2 },
  FIELD_CAPTURE: { label: 'Field Capture', color: 'text-teal-700 bg-teal-50 border-teal-100', icon: Camera },
  UPCOMING: { label: 'Upcoming', color: 'text-slate-700 bg-slate-50 border-slate-200', icon: Clock },
  SYNC_DATA_ATTENTION: { label: 'Sync / Data Attention', color: 'text-purple-700 bg-purple-50 border-purple-100', icon: RefreshCw },
};

export default function ProjectWorkspace() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'CompanyAdmin';
  const [view, setView] = useState('overview');
  const [project, setProject] = useState(null);
  const [myday, setMyday] = useState(null);
  const [exceptions, setExceptions] = useState(null);

  const go = (item) => {
    if (item.view) { setView(item.view); return; }
    if (item.toGlobal) { navigate(item.toGlobal); return; }
    const q = item.tab ? `?tab=${item.tab}` : '';
    navigate(`/app/projects/${projectId}/${item.to}${q}`);
  };

  useEffect(() => {
    axios.get(`${API}/projects/${projectId}`).then((r) => setProject(r.data)).catch(() => {});
  }, [projectId]);

  const loadMyday = useCallback(() => {
    axios.get(`${API}/projects/${projectId}/myday`).then((r) => setMyday(r.data)).catch(() => toast.error('Failed to load My Day'));
  }, [projectId]);
  const loadExceptions = useCallback(() => {
    axios.get(`${API}/projects/${projectId}/exceptions`).then((r) => setExceptions(r.data)).catch(() => toast.error('Failed to load exceptions'));
  }, [projectId]);

  useEffect(() => {
    if ((view === 'myday' || view === 'overview') && !myday) loadMyday();
    if ((view === 'exceptions' || view === 'overview') && !exceptions) loadExceptions();
  }, [view, myday, exceptions, loadMyday, loadExceptions]);

  return (
    <Layout>
      <div className="space-y-5" data-testid="project-workspace">
        {/* Project context header */}
        <div className="bg-gradient-to-r from-slate-900 to-indigo-900 text-white rounded-2xl p-5" data-testid="workspace-project-header">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-wide text-indigo-200">Project Workspace</div>
              <h1 className="text-2xl font-bold mt-0.5" data-testid="workspace-project-name">{project?.name || 'Loading…'}</h1>
              <div className="text-sm text-indigo-200 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                {project?.code && <span data-testid="workspace-project-code">Code: {project.code}</span>}
                {project?.client && <span>Client: {project.client}</span>}
                {project?.location && <span>Site: {project.location}</span>}
                <span>Role: {user?.role}</span>
              </div>
            </div>
            <button onClick={() => navigate('/app/projects')} className="text-sm px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20" data-testid="workspace-back-projects">All Projects</button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-5">
          {/* Left rail */}
          <nav className="lg:w-72 shrink-0 bg-white rounded-xl border border-slate-100 p-3 lg:sticky lg:top-4 self-start w-full overflow-x-auto" data-testid="workspace-rail">
            <div className="flex lg:block gap-4 lg:gap-0">
              {GROUPS.filter((g) => !g.adminOnly || isAdmin).map((g) => (
                <div key={g.title} className="lg:mb-3 min-w-[200px] lg:min-w-0">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 px-2 mb-1">{g.title}</div>
                  {g.items.map((it) => {
                    const active = it.view && view === it.view;
                    return (
                      <button key={it.label} onClick={() => go(it)} data-testid={`ws-nav-${(it.view || it.to || 'copilot')}${it.tab ? '-' + it.tab : ''}`}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm text-left transition-colors ${active ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-slate-600 hover:bg-slate-50'}`}>
                        <it.icon className="w-4 h-4 shrink-0" /> <span className="truncate">{it.label}</span>
                        {!it.view && <ChevronRight className="w-3.5 h-3.5 ml-auto text-slate-300" />}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </nav>

          {/* Main panel */}
          <div className="flex-1 min-w-0">
            {view === 'overview' && <Overview myday={myday} exceptions={exceptions} onOpen={setView} />}
            {view === 'myday' && <MyDay data={myday} onReload={loadMyday} />}
            {view === 'exceptions' && <Exceptions data={exceptions} onReload={loadExceptions} navigate={navigate} />}
            {view === 'copilot' && <Copilot projectId={projectId} navigate={navigate} />}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function StatCard({ label, value, testId, tone = 'slate' }) {
  return (
    <div className={`bg-white rounded-xl border border-slate-100 p-4`} data-testid={testId}>
      <div className={`text-2xl font-bold text-${tone}-900`}>{value ?? '—'}</div>
      <div className="text-xs text-slate-500 mt-1">{label}</div>
    </div>
  );
}

function Overview({ myday, exceptions, onOpen }) {
  const totalOverdue = (exceptions?.exceptions || []).reduce((a, e) => a + (e.overdue || 0), 0);
  const totalOpen = (exceptions?.exceptions || []).reduce((a, e) => a + (e.open || 0), 0);
  return (
    <div className="space-y-4" data-testid="workspace-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="My actionable items" value={myday?.totalActionable} testId="ov-myday-total" />
        <StatCard label="Overdue (project)" value={totalOverdue} testId="ov-overdue" />
        <StatCard label="Open records (visible)" value={totalOpen} testId="ov-open" />
        <StatCard label="Registers with exceptions" value={exceptions?.exceptions?.length} testId="ov-registers" />
      </div>
      <div className="flex gap-3">
        <button onClick={() => onOpen('myday')} className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700" data-testid="ov-open-myday">Open My Day</button>
        <button onClick={() => onOpen('exceptions')} className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 text-sm hover:bg-slate-50" data-testid="ov-open-exceptions">View Exceptions</button>
      </div>
      <p className="text-xs text-slate-400">Factual counts from authoritative registers. No project health score is computed.</p>
    </div>
  );
}

function MyDay({ data, onReload }) {
  if (!data) return <div className="text-slate-400 text-sm" data-testid="myday-loading">Loading My Day…</div>;
  return (
    <div className="space-y-4" data-testid="myday-panel">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-800">My Day <span className="text-sm text-slate-400">· {data.totalActionable} items</span></h2>
        <button onClick={onReload} className="text-sm text-indigo-600 hover:underline flex items-center gap-1" data-testid="myday-reload"><RefreshCw className="w-4 h-4" /> Refresh</button>
      </div>
      {data.sections.length === 0 && <div className="bg-white rounded-xl border border-slate-100 p-10 text-center text-slate-400" data-testid="myday-empty">Nothing assigned to you right now. 🎉</div>}
      {data.sections.map((sec) => {
        const meta = SECTION_META[sec.section] || SECTION_META.REQUIRES_MY_ACTION;
        return (
          <div key={sec.section} data-testid={`myday-section-${sec.section}`}>
            <div className={`inline-flex items-center gap-1.5 text-sm font-medium px-2.5 py-1 rounded-full border ${meta.color} mb-2`}>
              <meta.icon className="w-4 h-4" /> {meta.label} · {sec.count}
            </div>
            <div className="bg-white rounded-xl border border-slate-100 divide-y divide-slate-50">
              {sec.items.map((it) => (
                <a key={`${it.source}-${it.id}`} href={it.link} data-testid={`myday-item-${it.id}`}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-indigo-50/40 transition-colors">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-800 truncate">{it.number ? `${it.number} · ` : ''}{it.title}</div>
                    <div className="text-xs text-slate-400">{it.sourceLabel} · {it.reason}</div>
                  </div>
                  {it.status && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 shrink-0">{String(it.status).replace(/_/g, ' ')}</span>}
                  <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                </a>
              ))}
            </div>
          </div>
        );
      })}
      <p className="text-xs text-slate-400">{data.governanceNote}</p>
    </div>
  );
}

function Exceptions({ data, onReload, navigate }) {
  if (!data) return <div className="text-slate-400 text-sm" data-testid="exceptions-loading">Loading exceptions…</div>;
  return (
    <div className="space-y-4" data-testid="exceptions-panel">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-800">Project Exceptions</h2>
        <button onClick={onReload} className="text-sm text-indigo-600 hover:underline flex items-center gap-1" data-testid="exceptions-reload"><RefreshCw className="w-4 h-4" /> Refresh</button>
      </div>
      {data.exceptions.length === 0 && <div className="bg-white rounded-xl border border-slate-100 p-10 text-center text-slate-400" data-testid="exceptions-empty">No open exceptions in registers you can view.</div>}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {data.exceptions.map((e) => (
          <button key={e.key} onClick={() => navigate(e.link)} data-testid={`exception-${e.key}`}
            className="bg-white rounded-xl border border-slate-100 p-4 text-left hover:border-indigo-200 hover:shadow-sm transition-all">
            <div className="text-sm font-medium text-slate-700">{e.label}</div>
            <div className="flex items-center gap-4 mt-2">
              <span className="text-lg font-bold text-slate-900">{e.open} <span className="text-xs font-normal text-slate-400">open</span></span>
              {e.overdue > 0 && <span className="text-lg font-bold text-rose-600">{e.overdue} <span className="text-xs font-normal text-rose-400">overdue</span></span>}
            </div>
          </button>
        ))}
      </div>
      <p className="text-xs text-slate-400">{data.governanceNote}</p>
    </div>
  );
}

const GROUNDED_META = {
  GROUNDED: { label: 'Grounded', color: 'bg-green-100 text-green-800' },
  PARTIALLY_GROUNDED: { label: 'Partially grounded', color: 'bg-teal-100 text-teal-800' },
  INSUFFICIENT_DATA: { label: 'Insufficient data', color: 'bg-amber-100 text-amber-800' },
  ACCESS_RESTRICTED: { label: 'Access restricted', color: 'bg-rose-100 text-rose-700' },
  AI_UNAVAILABLE: { label: 'AI unavailable', color: 'bg-slate-200 text-slate-600' },
  ERROR: { label: 'Error', color: 'bg-rose-100 text-rose-700' },
};

function Copilot({ projectId, navigate }) {
  const [turns, setTurns] = React.useState([]);
  const [input, setInput] = React.useState('');
  const [suggestions, setSuggestions] = React.useState([]);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    axios.get(`${API}/projects/${projectId}/copilot/suggestions`).then((r) => setSuggestions(r.data.suggestions || [])).catch(() => {});
  }, [projectId]);

  const ask = async (q) => {
    const question = (q || input).trim();
    if (!question || busy) return;
    setInput('');
    setTurns((t) => [...t, { role: 'user', text: question }]);
    setBusy(true);
    try {
      const r = await axios.post(`${API}/projects/${projectId}/copilot/query`, { message: question });
      setTurns((t) => [...t, { role: 'copilot', data: r.data }]);
    } catch (e) {
      setTurns((t) => [...t, { role: 'copilot', data: { answer: 'Something went wrong.', status: 'ERROR', sources: [], limitations: [] } }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="copilot-panel">
      <div className="flex items-center gap-2">
        <Bot className="w-5 h-5 text-indigo-600" />
        <h2 className="text-lg font-semibold text-slate-800">Construction Copilot</h2>
        <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">Project-scoped</span>
      </div>
      <p className="text-xs text-slate-400" data-testid="copilot-governance-note">Answers use only records you are authorized to view on this project. AI answers are not authoritative project records.</p>

      {turns.length === 0 && (
        <div data-testid="copilot-suggestions" className="flex flex-wrap gap-2">
          {suggestions.map((s) => (
            <button key={s} onClick={() => ask(s)} className="text-sm px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-600 hover:border-indigo-300 hover:text-indigo-700" data-testid="copilot-suggestion">{s}</button>
          ))}
        </div>
      )}

      <div className="space-y-3" data-testid="copilot-conversation">
        {turns.map((t, i) => t.role === 'user' ? (
          <div key={i} className="flex justify-end"><div className="bg-indigo-600 text-white text-sm rounded-2xl rounded-br-sm px-4 py-2 max-w-[80%]" data-testid="copilot-user-msg">{t.text}</div></div>
        ) : (
          <div key={i} className="bg-white rounded-2xl rounded-bl-sm border border-slate-100 p-4 max-w-[92%]" data-testid="copilot-answer">
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-xs px-2 py-0.5 rounded-full ${(GROUNDED_META[t.data.status] || GROUNDED_META.ERROR).color}`} data-testid="copilot-groundedness">{(GROUNDED_META[t.data.status] || GROUNDED_META.ERROR).label}</span>
              {t.data.aiUsed && <span className="text-xs text-slate-400">AI-summarized</span>}
            </div>
            <div className="text-sm text-slate-800 whitespace-pre-wrap">{t.data.answer}</div>
            {t.data.sources?.length > 0 && (
              <div className="mt-3 space-y-1" data-testid="copilot-sources">
                <div className="text-xs font-semibold text-slate-500 uppercase">Sources ({t.data.sources.length})</div>
                {t.data.sources.slice(0, 10).map((s, j) => (
                  <button key={j} onClick={() => navigate(s.deepLink)} data-testid="copilot-source" className="w-full text-left flex items-center gap-2 text-sm px-2 py-1.5 rounded-lg hover:bg-indigo-50/50">
                    <span className="font-mono text-xs text-indigo-800">{s.displayNumber || s.recordType}</span>
                    <span className="text-slate-600 truncate flex-1">{s.title}</span>
                    {s.status && <span className="text-xs text-slate-400">{String(s.status).replace(/_/g, ' ')}</span>}
                    <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
                  </button>
                ))}
              </div>
            )}
            {t.data.warnings?.length > 0 && <div className="mt-2 text-xs text-amber-700">{t.data.warnings.join(' ')}</div>}
            {t.data.limitations?.length > 0 && <div className="mt-2 text-[11px] text-slate-400">{t.data.limitations[0]}</div>}
          </div>
        ))}
        {busy && <div className="text-sm text-slate-400" data-testid="copilot-loading">Thinking…</div>}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-2 sticky bottom-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about this project…" data-testid="copilot-input"
          className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-500" />
        <button type="submit" disabled={busy} data-testid="copilot-send" className="px-4 py-2.5 bg-indigo-600 text-white rounded-xl text-sm hover:bg-indigo-700 disabled:opacity-50">Ask</button>
      </form>
    </div>
  );
}
